/**
 * DomainStep.jsx
 * --------------
 * Renders a single assessment domain — its intro text, three scored
 * questions, and the perception gap question.
 *
 * This component is rendered once per domain. CawoodAssessment.jsx
 * controls which domain is currently shown using a domain index.
 *
 * RESPONSIBILITIES:
 *   - Display the domain intro text
 *   - Render three 1–5 rating scale questions
 *   - Render the perception gap question with its five-option button row
 *   - Validate that all questions are answered before allowing Next
 *   - Pass completed domain answers up to CawoodAssessment
 *
 * PROPS:
 *   domain       {Object}   - A single domain object from DOMAINS in questions.js
 *   domainIndex  {number}   - Zero-based index of this domain (0–5)
 *   totalDomains {number}   - Total number of domains (6)
 *   existingAnswers {Object} - Previously saved answers for this domain
 *                             (allows Back navigation without losing answers)
 *   onComplete   {function} - Called with { domainAnswers, perceptionAnswer }
 *                             when the respondent clicks Next
 *   onBack       {function} - Called when the respondent clicks Back
 *
 * STYLING:
 *   All styles in cawood-assessment.css under .ca-domain__* and .ca-question__*
 *
 * Connected files:
 *   - questions.js          provides DOMAINS and PERCEPTION_GAP_OPTIONS
 *   - CawoodAssessment.jsx  manages domain index and answer state
 *   - cawood-assessment.css provides all styles
 */

import { useState } from "react";
import { PERCEPTION_GAP_OPTIONS } from "../data/questions.js";

const SCALE_OPTIONS = [
  { value: 1, label: "Not true of us" },
  { value: 2, label: "Rarely true" },
  { value: 3, label: "Sometimes true" },
  { value: 4, label: "Mostly true" },
  { value: 5, label: "Completely true" },
];

// ─── Sub-component: ScaleQuestion ─────────────────────────────────────────────

/**
 * Renders a single rated question with a 1–5 button scale.
 *
 * @param {Object}   question     - Question object from domain.questions
 * @param {number}   value        - Currently selected value (1–5) or undefined
 * @param {function} onChange     - Called with the selected numeric value
 * @param {boolean}  showError    - Whether to show the unanswered validation state
 */
function ScaleQuestion({ question, value, onChange, showError }) {
  return (
    <div className="ca-question">

      {/* Question type label (Structural / Operational / Strategic) */}
      <div className="ca-question__type-label">
        {question.type}
      </div>

      {/* Question text */}
      <div className="ca-question__text">
        {question.text}
      </div>

      {/* 1–5 rating scale */}
      <div className="ca-scale" role="radiogroup" aria-label={question.text}>
        {SCALE_OPTIONS.map((opt) => (
          <label
            key={opt.value}
            className={[
              "ca-scale__option",
              value === opt.value ? "ca-scale__option--selected" : "",
            ].join(" ")}
          >
            <input
              type="radio"
              name={question.id}
              value={opt.value}
              checked={value === opt.value}
              onChange={() => onChange(opt.value)}
              style={{ position: "absolute", opacity: 0, width: 0, height: 0 }}
              aria-label={`${opt.value} — ${opt.label}`}
            />
            <span className="ca-scale__value">{opt.value}</span>
            <span className="ca-scale__label">{opt.label}</span>
          </label>
        ))}
      </div>

      {/* Validation — shown only if the respondent tries to proceed unanswered */}
      {showError && (
        <span className="ca-form__error-message">
          Please rate this statement before continuing.
        </span>
      )}

    </div>
  );
}

// ─── Main component: DomainStep ───────────────────────────────────────────────

export default function DomainStep({
  domain,
  domainIndex,
  totalDomains,
  existingAnswers,
  onComplete,
  onBack,
}) {

  // Initialise answers from existingAnswers so Back navigation restores state
  const [questionAnswers, setQuestionAnswers] = useState(
    existingAnswers?.questions ?? {}
  );
  const [perceptionAnswer, setPerceptionAnswer] = useState(
    existingAnswers?.perception ?? null
  );

  // Only show validation errors after the respondent has attempted to proceed
  const [showErrors, setShowErrors] = useState(false);

  // ── Answer handlers ────────────────────────────────────────────────────────

  function handleQuestionChange(questionId, value) {
    setQuestionAnswers((prev) => ({ ...prev, [questionId]: value }));
  }

  function handlePerceptionChange(value) {
    setPerceptionAnswer(value);
  }

  // ── Validation ─────────────────────────────────────────────────────────────
  // All three questions and the perception gap must be answered.

  function allAnswered() {
    const questionsAnswered = domain.questions.every(
      (q) => questionAnswers[q.id] !== undefined
    );
    return questionsAnswered && perceptionAnswer !== null;
  }

  // ── Submit handler ─────────────────────────────────────────────────────────

  function handleNext() {
    if (!allAnswered()) {
      setShowErrors(true);
      return;
    }
    onComplete({ questions: questionAnswers, perception: perceptionAnswer });
  }

  // ── Progress calculation ───────────────────────────────────────────────────
  // Progress bar reflects position within the full assessment.
  // Intake = 0%. Domain 1 start = ~14%. Domain 6 complete = 100%.

  const progressPercent = Math.round(((domainIndex + 1) / totalDomains) * 100);

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="ca-assessment__step">

      {/* Progress bar */}
      <div className="ca-progress">
        <div className="ca-progress__label">
          <span>
            Domain {domainIndex + 1} of {totalDomains}
          </span>
          <span>{progressPercent}% complete</span>
        </div>
        <div className="ca-progress__track">
          <div
            className="ca-progress__fill"
            style={{ width: `${progressPercent}%` }}
            role="progressbar"
            aria-valuenow={progressPercent}
            aria-valuemin={0}
            aria-valuemax={100}
          />
        </div>
      </div>

      {/* Domain header */}
      <div className="ca-domain__header">
        <div className="ca-domain__badge">{domain.label}</div>
        <h2>{domain.label}</h2>
      </div>

      {/* Domain intro text */}
      <div className="ca-domain__intro">
        {domain.intro}
      </div>

      {/* Scored questions */}
      <div className="ca-questions">
        {domain.questions.map((question) => (
          <ScaleQuestion
            key={question.id}
            question={question}
            value={questionAnswers[question.id]}
            onChange={(val) => handleQuestionChange(question.id, val)}
            showError={showErrors && questionAnswers[question.id] === undefined}
          />
        ))}
      </div>

      {/* Perception gap question */}
      <div className="ca-perception">

        <div className="ca-perception__label">Perception gap</div>

        <div className="ca-perception__question">
          {domain.perceptionGap.text}
        </div>

        <div className="ca-perception__options">
          {PERCEPTION_GAP_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              className={[
                "ca-perception__option",
                perceptionAnswer === option.value
                  ? "ca-perception__option--selected"
                  : "",
              ].join(" ")}
              onClick={() => handlePerceptionChange(option.value)}
              aria-pressed={perceptionAnswer === option.value}
            >
              {option.label}
            </button>
          ))}
        </div>

        {/* Validation error if perception gap unanswered */}
        {showErrors && perceptionAnswer === null && (
          <span
            className="ca-form__error-message"
            style={{ color: "var(--ca-colour-coral-soft)", marginTop: "8px", display: "block" }}
          >
            Please select an option before continuing.
          </span>
        )}

      </div>

      {/* Navigation */}
      <div className="ca-nav">
        <button
          type="button"
          className="ca-btn ca-btn--secondary"
          onClick={onBack}
        >
          ← Back
        </button>

        <button
          type="button"
          className="ca-btn ca-btn--primary"
          onClick={handleNext}
        >
          {domainIndex < totalDomains - 1 ? "Next domain →" : "See my results →"}
        </button>
      </div>

    </div>
  );
}
